import { useState, useEffect } from 'react';
import PageContent, { PageBreadcrumbProps } from '../PageContent';
import { ODataGrid, TitlelessCard } from '../../components';
import { GridCellProps, GridColumn, GridRowClickEvent } from '@progress/kendo-react-grid';
import CTEyeService from '../../services/CTEyeService';
import CompanyService from '../../services/CompanyService';
import { router, routes } from '../../app/routes';
import CTEyeRootSubmenu from './CTEyeRootSubmenu';
import { ClientRegistrationStatus, CompanyModel } from '../../data/entities';
import { Form } from 'react-bootstrap';
import {  DotBadge } from './components';
import { formatDateTime } from '../../app/dateHelpers';

export function CTEyeActiveClients() {
  const title = "Registered Agents";
  const breadcrumbs: PageBreadcrumbProps = {
    showHome: true,
    values: [
      { label: "CTEye Agents", url: routes.cteye },
      { label: title }
    ]
  };
  const service = new CTEyeService();
  const submenus = <CTEyeRootSubmenu />;

  const [companies, setCompanies] = useState<CompanyModel[]>([]);
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState<number | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const data = await new CompanyService().getAllCompanies();
      setCompanies(data);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const getData = async (oDataQuery: string) => {
    let filter = `(registrationStatus eq 'Registered' or registrationStatus eq 'Disabled')`;
    if (selectedCompanyFilter !== null) {
      filter += ` and companyId eq ${selectedCompanyFilter}`;
    }

    const gridFilterMatch = oDataQuery.match(/\$filter=([^&]*)/);
    if (gridFilterMatch) {
      filter += ` and (${gridFilterMatch[1]})`;
      oDataQuery = oDataQuery.replace(`$filter=${gridFilterMatch[1]}`, `$filter=${filter}`);
    } else {
      oDataQuery = oDataQuery ? `${oDataQuery}&$filter=${filter}` : `$filter=${filter}`;
    }

    if (!oDataQuery.includes('$expand=')) {
      oDataQuery += '&$expand=Company';
    }

    return await service.getClientsOData(oDataQuery);
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleRowClick = (e: GridRowClickEvent) => {
    router.navigateWithParams(
      routes.cteyeClientDetails,
      new Map([["id", e.dataItem.id]])
    );
  };

  const companyCell = (props: GridCellProps) => {
    const companyName = props.dataItem.companyName ?? props.dataItem.company?.name ?? '—';
    return (
      <td>
        {companyName === '—' ? (
          <span className="text-muted">{companyName}</span>
        ) : (
          <span>{companyName}</span>
        )}
      </td>
    );
  };

  const versionCell = (props: GridCellProps) => {
    const version = props.dataItem.runningVersion || '—';
    return <td><code className="text-muted">{version}</code></td>;
  };

  const lastSeenCell = (props: GridCellProps) =>{
    return (
      <td className="text-secondary small">
        {props.dataItem.lastSeenDate
          ? formatDateTime(new Date(props.dataItem.lastSeenDate))
          : "—"}
      </td>
    )
  }

  return (
    <PageContent title={title} breadcrumbs={breadcrumbs} submenu={submenus}>
      <div className="row mb-3">
        <div className="col-md-4">
          <Form.Group>
            <Form.Label className="small text-muted">Filter by Company</Form.Label>
            <Form.Select
              value={selectedCompanyFilter ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedCompanyFilter(value === '' ? null : Number(value));
                handleRefresh();
              }}
              size="sm"
            >
              <option value="">All Companies</option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>
      </div>
      <TitlelessCard className="p-0" bodyClassName="p-0">
        <ODataGrid 
          key={refreshKey}
          getData={getData}
          onRowClick={handleRowClick}
          pageSize={20}
          pageable={{ buttonCount: 5 }}
        >
          <GridColumn 
            title="Agent" 
            field="description" 
            sortable={true}
            filterable ={true}
          />
          <GridColumn 
            title="Company" 
            field="company/name" 
            sortable={true}
            filterable ={true}
            cell={companyCell}
          />
          <GridColumn 
            title="Status" 
            field="registrationStatus" 
            sortable={false}
            filterable ={false}
             cell={(props) => (
              <td>
                <DotBadge
                  variant={props.dataItem.registrationStatus === ClientRegistrationStatus.Registered ? "active" : "inactive"}
                  label={props.dataItem.registrationStatus}
                />
              </td>
            )}
          />
          <GridColumn
            title="Version"
            field="runningVersion"
            sortable={true}
            filterable={true}
            cell={versionCell}
          />
          <GridColumn 
            title="Last Seen" 
            field="lastSeenDate" 
            sortable={false}
            filterable ={false}
            cell={lastSeenCell}
          />
        </ODataGrid>
      </TitlelessCard>
    </PageContent>
  );
}
